import { z } from 'zod';

import {
  FormBlockBaseConfigShape,
  FormTemplateConfigSchema,
  formBlockEntityOverrideCheck,
  formBlockEntityOverrideRefineMessage,
} from './config.js';
import { isDefined } from '../../../utils.js';
import { BaseFlowBlockSchema, BlockType, DurationSchema } from '../base/baseSchema.js';
import { BlockValueUuidSchema } from '../base/blockValueUuidSchema.js';

export enum FormV2Mode {
  AddOnly = 'AddOnly',
  AddAndWait = 'AddAndWait',
  AddAndWaitForCompletion = 'AddAndWaitForCompletion',
}

export function isFormV2WaitMode(mode: FormV2Mode): boolean {
  return mode === FormV2Mode.AddAndWait || mode === FormV2Mode.AddAndWaitForCompletion;
}

// FormV2 = v1 Form + (optional) WaitForFormCompletion fused into one block.
// `mode` controls whether the block waits for the form set to reach a terminal
// state. `timeout` is required whenever the block waits (`AddAndWait` or
// `AddAndWaitForCompletion`).
export const FormV2BlockConfigSchema = FormBlockBaseConfigShape.omit({ runMode: true })
  .extend({
    formTemplateConfigs: z
      .array(FormTemplateConfigSchema)
      .min(1, 'FormV2 requires at least one form template'),
    mode: z.enum(FormV2Mode).optional().default(FormV2Mode.AddAndWaitForCompletion),
    timeout: DurationSchema.optional(),
    notificationSchemeIdOverride: BlockValueUuidSchema({ isRequired: false }).nullable().optional(),
  })
  .refine(formBlockEntityOverrideCheck, formBlockEntityOverrideRefineMessage)
  .refine((config) => !isFormV2WaitMode(config.mode) || isDefined(config.timeout), {
    message: 'A timeout is required when the FormV2 block waits for the form set.',
    path: ['timeout'],
  });

export type FormV2BlockConfig = z.infer<typeof FormV2BlockConfigSchema>;

export const FormV2BlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.FormV2),
  config: FormV2BlockConfigSchema,
});
export type FormV2Block = z.infer<typeof FormV2BlockSchema>;
